/**
 * UI-internal view model for Customer 360 — adapts the backend
 * `CustomerProfile` (see Customer360.types.ts) into a flatter shape that
 * the Panel/Tabbed/Timeline components render against.
 *
 * The wire format is the exact JSON from the backend; this adapter is a
 * presentation concern only. It handles both the "simplified" and the
 * "full" response shapes (which differ in a bunch of places — raw Connect
 * Case field arrays, richer device/cook/shipment rows, and pre-merge
 * `raw_profiles`). Callers should not need to know which shape arrived.
 *
 * TODO: This entire viewmodel is Traeger-specific. When model-driven views
 * are implemented (Phase 2), the impl-repo Pydantic model + json-render
 * schema will replace this file — no per-tenant viewmodel in the base webapp.
 */
import type {
  CustomerProfile,
  CaseItem as RawCaseItem,
  ConnectedDevice as RawConnectedDevice,
  CookItem as RawCookItem,
  RawProfile,
  ShipmentItem as RawShipmentItem,
} from './Customer360.types';
import { getCaseField } from './Customer360.types';

// ---------------------------------------------------------------------------
// View-model types
// ---------------------------------------------------------------------------

export interface MergedCustomer360 {
  identity: {
    name: string;
    emails: string[];
    phones: string[];
    address?: string;
    accountStatus?: string;
    accountType?: string;
    memberSince?: string;
    accountNumber?: string;
    businessName?: string;
    personalEmail?: string;
    sfAccountId?: string;
    sfContactId?: string;
    primaryProfileId: string;
    mergedProfileIds: string[];
  };
  stats: {
    ytdSales?: number;
    totalCases: number;
    openCases: number;
    registeredProducts: number;
    totalOrders: number;
  };
  alert?: { message: string; type: 'info' | 'warning' | 'error' };
  cases: CaseItem[];
  orders: OrderItem[];
  shipments: ShipmentItem[];
  assets: AssetItem[];
  connectedDevices: ConnectedDevice[];
  sourceProfiles: SourceProfile[];
  /** Richer pre-merge profile rows (mailing/shipping/billing addresses, SF ids). */
  rawProfiles: RawProfile[];
  cookSummary?: CookSummary;
  cooks: CookViewItem[];
}

export interface CookViewItem {
  cookId: string;
  startedAt: string | null;
  durationMinutes: number | null;
  ambientMin: number | null;
  ambientMax: number | null;
  probeMin: number | null;
  probeMax: number | null;
  goalTemp: number | null;
  criticalAlerts: number;
  warningAlerts: number;
  lidOpenings: number;
  setpointChanges: number;
  problemType: string | null;
  deviceFriendlyName: string | null;
  deviceTradeName: string | null;
}

export interface CaseItem {
  caseId: string;
  referenceNumber: string;
  title: string;
  status: 'open' | 'closed' | 'pending';
  createdAt: string;
  lastUpdated?: string;
  closedAt?: string;
  reopenedAt?: string;
  brand?: string;
  asset?: string;
  summary?: string;
  productsOrdered?: string;
  orderNumber?: string;
  // Routing / classification
  channel?: string;
  caseReason?: string;
  caseRecordType?: string;
  escalateToQueue?: string;
  contactId?: string;
  // Order / parts cross-references
  itemSku?: string;
  serviceOrderNumber?: string;
  trackingNumber?: string;
  // Notes
  customerFacingNotes?: string;
  internalComments?: string;
  // External link
  loupeLink?: string;
  // Connect agent attribution (resolved server-side from the case's
  // contact_id via Connect describe-contact → describe-user → hierarchy).
  agentName?: string;
  bpo?: string;
  site?: string;
}

export interface ShipmentItem {
  orderNumber: string | number;
  orderDate: string;
  shipDate?: string | null;
  productDescription: string;
  productSku: string;
  trackingNumber?: string | null;
  shipperName?: string | null;
  carrierName?: string | null;
  isDelivered?: boolean | null;
  isDeliveredOnTime?: boolean | null;
  deliveredDate?: string | null;
  shipStatus?: string | null;
  daysLate?: number | null;
  appointmentChanges?: number | null;
  destinationCity?: string | null;
  destinationState?: string | null;
  destinationPostalCode?: string | null;
  destinationAddress1?: string | null;
  isResidential?: boolean | null;
  warehouseDescription?: string | null;
  quantity?: string;
  priceUsd?: number | null;
  sourceSystem?: string | null;
  customerPoNumber?: string | null;
  shippingAddress?: string | null;
  paymentTerms?: string | null;
}

export interface OrderItem {
  orderNumber: string;
  date: string;
  status: string;
  total: number;
  isWarranty: boolean;
  shippingAddress?: string;
  items?: string[];
}

export interface AssetItem {
  serialNumber: string;
  /** SKU / model description (e.g. "WOODRIDGE PRO"). Used as the row
   *  header so agents can spot the model at a glance. */
  productName: string;
  /** Customer-facing nickname (device friendly name, falling back to the
   *  Salesforce asset name) — surfaced as a secondary line so the SKU can
   *  carry the header. May be omitted when it would just duplicate the
   *  header text. */
  nickname?: string;
  productSku?: string;
  productFamily?: string;
  productGroup?: string;
  status: string;
  registeredDate?: string;
  purchaseDate?: string;
  purchaseStore?: string | null;
  msrp?: number | null;
  warrantyExpiration?: string | null;
  warrantyVoided?: boolean;
  warrantyPassed?: boolean;
  warrantyStartDate?: string | null;
  returnable?: boolean;
  // Enriched
  firmwareVersion?: string | null;
  configVersion?: string | null;
  deviceUuid?: string | null;
  deviceFriendlyName?: string | null;
  lastConnected?: string | null;
  categoryLevels?: string[];
  // Connectivity (from grill-details lambda — only present in /short payload).
  wifiName?: string | null;
  wifiSignalStrength?: number | null;
  controllerType?: string | null;
  controllerFamily?: string | null;
  grillLine?: string | null;
  modelFamily?: string | null;
  isCourtesyItem?: boolean | null;
  // Structured product category from the Lambda payload.
  categoryL1?: string | null;
  categoryL2?: string | null;
  categorySeries?: string | null;
  categoryModel?: string | null;
  // Linked connected-device fields (merged in from data.connected_devices via
  // device_uuid). Lets a single Products view stand in for the old separate
  // Connected Devices section — Search List takes the same approach.
  ownerName?: string | null;
  ownerEmail?: string | null;
  fleetEntryDate?: string | null;
  manufacturedAt?: string | null;
  skillLevel?: string | null;
  timeZone?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  googleMapsLink?: string | null;
  totalCooks?: number | null;
}

export interface ConnectedDevice {
  /**
   * Stable identifier — populated from `controller_serial` when present
   * (simplified payload), otherwise from `device_uuid` (full payload).
   * Use {@link identifierKind} to render the right label.
   */
  controllerSerial: string;
  /** Indicates whether {@link controllerSerial} holds a hardware serial or
   *  a platform-issued UUID, so the UI can label it correctly. */
  identifierKind: 'serial' | 'uuid';
  tradeName: string;
  friendlyName?: string;
  firmwareVersion?: string | null;
  lastConnected?: string;
  // Enriched
  ownerName?: string | null;
  ownerEmail?: string | null;
  timeZone?: string | null;
  fleetEntryDate?: string | null;
  manufacturedAt?: string | null;
  skillLevel?: string | null;
  googleMapsLink?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  totalCooks?: number | null;
}

export interface SourceProfile {
  profileId: string;
  email?: string;
  phone?: string;
  hasName: boolean;
  caseCount: number;
  assetCount: number;
}

export interface CookSummary {
  totalCooks: number;
  totalCriticalAlerts: number;
  totalWarningAlerts: number;
  problemCooks: number;
  avgDurationMinutes: number | null;
  lastCookDate: string | null;
  runningTotalAllTime: number | null;
}

// ---------------------------------------------------------------------------
// Carrier tracking URL helpers
// TODO: Traeger-specific carrier codes (FXHD, UUMI, etc.). Other tenants may
// have different shippers. Move to tenant config or a shared carrier database.
// ---------------------------------------------------------------------------

export function getTrackingUrl(
  shipperName: string | null | undefined,
  trackingNumber: string | null | undefined
): string | null {
  if (!shipperName || !trackingNumber) return null;
  const s = shipperName.toLowerCase();
  const n = encodeURIComponent(trackingNumber.trim());
  if (/fedex|fxhd|fxg|fxsm|fxfe/.test(s)) {
    return `https://www.fedex.com/fedextrack/?trknbr=${n}`;
  }
  if (/ups\s*mail\s*innovations|uumi/.test(s)) {
    return `https://www.ups.com/mailinnovations/track?tracknum=${n}`;
  }
  if (/\bups\b/.test(s)) {
    return `https://www.ups.com/track?tracknum=${n}`;
  }
  if (/usps|us\s*postal/.test(s)) {
    return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${n}`;
  }
  if (/dhl/.test(s)) {
    return `https://www.dhl.com/global-en/home/tracking/tracking-express.html?tracking-id=${n}`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Adapter
// ---------------------------------------------------------------------------

const STATUS_MAP: Record<string, 'open' | 'closed' | 'pending'> = {
  open: 'open',
  closed: 'closed',
  pending: 'pending',
  new: 'open',
  resolved: 'closed',
};

function normalizeStatus(s?: string): 'open' | 'closed' | 'pending' {
  return STATUS_MAP[(s ?? '').toLowerCase()] ?? 'closed';
}

// Match the server-side formatter (calltree-tools-ui customer360.ts): treat
// "n/a"/"00000"/"0"/"" as junk, and only consider an address usable when at
// least 3 non-junk fields are present. Without the threshold a raw profile
// with just `{ Country: "United States" }` would shadow a sibling profile
// that actually has a real street address — which is exactly how /full
// payloads can ship merged-profile data.
const ADDRESS_JUNK = new Set(['n/a', '00000', '0', '']);
function formatAddress(a: RawProfile['MailingAddress']): string | undefined {
  if (!a) return undefined;
  const parts = [
    [a.Address1, a.Address2, a.Address3].filter(Boolean).join(' '),
    a.City,
    [a.State, a.PostalCode].filter(Boolean).join(' '),
    a.Country,
  ];
  const cleaned = parts.filter((p): p is string => !!p && !ADDRESS_JUNK.has(p.toLowerCase()));
  if (cleaned.length < 3) return undefined;
  return cleaned.join(', ');
}

function pickAddress(raw: RawProfile[]): string | undefined {
  // Prefer mailing addresses across all profiles before falling back to
  // shipping/billing/default — Search List shows the mailing address.
  const buckets: Array<keyof RawProfile> = [
    'MailingAddress',
    'ShippingAddress',
    'BillingAddress',
    'Address',
  ];
  for (const bucket of buckets) {
    for (const rp of raw) {
      const addr = formatAddress(rp[bucket] as RawProfile['MailingAddress']);
      if (addr) return addr;
    }
  }
  return undefined;
}

function adaptCase(c: RawCaseItem): CaseItem {
  return {
    caseId: c.caseId ?? c.case_id ?? '',
    referenceNumber: getCaseField(c, 'reference_number') ?? '',
    title: getCaseField(c, 'title') ?? '',
    status: normalizeStatus(getCaseField(c, 'status')),
    createdAt: getCaseField(c, 'created_datetime') ?? '',
    lastUpdated: getCaseField(c, 'last_updated_datetime'),
    closedAt: getCaseField(c, 'last_closed_datetime'),
    reopenedAt: getCaseField(c, 'last_reopened_datetime'),
    brand: getCaseField(c, 'brand'),
    asset: getCaseField(c, 'asset'),
    summary: getCaseField(c, 'summary'),
    productsOrdered: getCaseField(c, 'products_ordered'),
    orderNumber: getCaseField(c, 'order_number'),
    channel: getCaseField(c, 'channel'),
    caseReason: getCaseField(c, 'case_reason'),
    caseRecordType: getCaseField(c, 'case_record_type'),
    escalateToQueue: getCaseField(c, 'escalate_to_queue'),
    contactId: getCaseField(c, 'contact_id'),
    itemSku: getCaseField(c, 'item_sku'),
    serviceOrderNumber: getCaseField(c, 'service_order_number'),
    trackingNumber: getCaseField(c, 'tracking_number'),
    customerFacingNotes: getCaseField(c, 'customer_facing_notes'),
    internalComments: getCaseField(c, 'internal_comments'),
    loupeLink: getCaseField(c, 'loupe_link'),
    agentName: getCaseField(c, 'agent_name'),
    bpo: getCaseField(c, 'bpo'),
    site: getCaseField(c, 'site'),
  };
}

function parseNumber(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number.parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

function adaptShipment(sh: RawShipmentItem): ShipmentItem {
  return {
    orderNumber: sh.order_number,
    orderDate: sh.order_date,
    // Prefer the datetime version when available so the UI can show time
    // (and timezone) instead of just the date. Falls back to ship_date
    // when only the date is recorded.
    shipDate: sh.shipped_datetime ?? sh.ship_date,
    productDescription: sh.product_description,
    productSku: sh.product_sku,
    trackingNumber:
      sh.tracking_number ??
      sh.ship_header_tracking_number ??
      sh.consolidated_tracking_number ??
      null,
    shipperName: sh.carrier_name ?? sh.shipper_name ?? sh.uf_shipper_name,
    carrierName: sh.carrier_name,
    isDelivered: sh.is_delivered,
    isDeliveredOnTime: sh.is_delivered_on_time,
    deliveredDate: sh.delivered_date ?? sh.delivered_datetime,
    shipStatus: sh.ship_status,
    daysLate: parseNumber(sh.days_late),
    appointmentChanges: parseNumber(sh.number_of_appointment_changes),
    destinationCity: sh.destination_city,
    destinationState: sh.destination_state_province,
    destinationPostalCode: sh.destination_postal_code,
    destinationAddress1: sh.destination_address_1,
    isResidential: sh.is_residential_delivery,
    warehouseDescription: sh.warehouse_description ?? sh.warehouse_name ?? sh.plant_name,
    quantity:
      typeof sh.shipped_quantity === 'string'
        ? sh.shipped_quantity
        : sh.shipped_quantity?.toString(),
    priceUsd: parseNumber(sh.extended_price_usd),
    sourceSystem: sh.source_system,
    customerPoNumber: sh.customer_po_number,
    paymentTerms: sh.payment_terms_description,
  };
}

function adaptDevice(d: RawConnectedDevice): ConnectedDevice {
  const lat = d.device_latitude ?? null;
  const lng = d.device_longitude ?? null;
  // Construct the map URL with the official Maps Search API (`?api=1`) so
  // the link works reliably across desktop and mobile and drops a pin at
  // the exact coordinates. The `@lat,lng,zoom?` form we used to emit
  // sometimes opens to a blank map, and trailing-`?` URLs break some
  // browsers' URL handling. The same shape is used for the sidebar
  // address link, so behavior is consistent.
  const mapsLink =
    lat != null && lng != null
      ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
      : (d.google_maps_link ?? null);
  // Prefer hardware controller_serial when present; otherwise fall back to
  // the platform-issued device_uuid and tag the kind so the UI labels it as
  // "UUID" instead of mislabeling a UUID as a serial number.
  const identifier = d.controller_serial ?? d.device_uuid ?? '';
  const identifierKind: 'serial' | 'uuid' = d.controller_serial ? 'serial' : 'uuid';
  return {
    controllerSerial: identifier,
    identifierKind,
    tradeName: d.trade_name ?? '',
    friendlyName: d.friendly_name ?? d.device_friendly_name ?? undefined,
    firmwareVersion: d.firmware_version ?? d.config_version,
    lastConnected: d.last_connected ?? d.last_connected_date ?? undefined,
    ownerName: d.owner_full_name,
    ownerEmail: d.device_owner_email,
    timeZone: d.device_time_zone_name,
    fleetEntryDate: d.fleet_entry_date,
    manufacturedAt:
      d.manufactured_at && !d.manufactured_at.startsWith('1970') ? d.manufactured_at : null,
    skillLevel: d.reported_skill_level,
    googleMapsLink: mapsLink,
    latitude: lat,
    longitude: lng,
  };
}

function adaptAsset(
  a: CustomerProfile['assets'][number],
  enrichmentBySerial?: Map<string, CustomerProfile['assets'][number]>,
  profileEmail?: string,
  /** Connected devices keyed by device_uuid — used to merge IoT-only
   *  fields (owner, fleet date, lat/lng, …) into the asset so a single
   *  Products view can replace the old separate Connected Devices tab. */
  deviceByUuid?: Map<string, ConnectedDevice>
): AssetItem {
  // Connect Customer-Profiles serves Salesforce assets in PascalCase
  // (`SerialNumber`, `Name`, `ProductCode`, …). The agent app's backend
  // pre-flattens these to snake_case server-side, but other backends
  // (e.g. calltree-tools-ui's /full route) pass them through raw. Fall back
  // to the PascalCase fields so both shapes render.
  const serial = a.serial_number ?? a.SerialNumber ?? a.serialnumber ?? '';
  // Asset-level fields don't include the Redshift enrichment when the asset
  // comes from raw Customer Profiles — look it up by serial when available.
  const enr = serial ? enrichmentBySerial?.get(serial) : undefined;
  // Only surface device-level fields when the IoT owner matches this profile
  // AND the controller has ever connected. Guards two cases: an asset
  // transferred in SF but not re-registered in the Traeger app (owner
  // mismatch), and a controller that was replaced so the SF-linked UUID is
  // stale (no last_connected_date).
  const deviceOwner = (enr?.device_owner_email ?? a.device_owner_email ?? '').toLowerCase();
  const profileOwner = (profileEmail ?? '').toLowerCase();
  const lastConnected = a.last_connected_date ?? a.last_connected ?? enr?.last_connected_date;
  const deviceBelongsToProfile =
    !!lastConnected && !!deviceOwner && !!profileOwner && deviceOwner === profileOwner;

  // The structured `category` object (`{l1, l2, series, model}`) is sent by
  // the c360_search Lambda; flat `product_category_level_*` fields come from
  // the enrichment or the /short formatter. Support both.
  const cat = typeof a.category === 'object' && a.category !== null ? a.category : undefined;
  const levels = [
    a.product_category_level_1 ?? enr?.product_category_level_1 ?? cat?.l1,
    a.product_category_level_2 ?? enr?.product_category_level_2 ?? cat?.l2,
    a.product_category_level_3 ?? enr?.product_category_level_3 ?? cat?.series,
    a.product_category_level_4 ?? enr?.product_category_level_4 ?? cat?.model,
  ].filter((l): l is string => Boolean(l));
  // SKU/model name leads — that's what agents look up against parts and
  // recipes. Nickname goes to the secondary line so the row carries both.
  const productLabel =
    a.product_description ??
    a.ProductDescription ??
    a.product_name__c ??
    a.product_code ??
    a.ProductCode ??
    a.product2_code ??
    a.model_family ??
    enr?.product_sku ??
    a.asset_name ??
    a.name ??
    a.Name ??
    enr?.asset_name ??
    '';
  const candidateNickname =
    a.device_friendly_name ?? enr?.device_friendly_name ?? a.asset_name ?? a.name ?? a.Name;
  const nickname =
    candidateNickname && candidateNickname !== productLabel ? candidateNickname : undefined;
  return {
    serialNumber: serial,
    productName: productLabel,
    nickname,
    productSku: a.product_sku ?? a.product_code ?? a.ProductCode ?? a.product2_name ?? a.product2_code ?? enr?.product_sku,
    productGroup: a.product_group ?? enr?.product_group,
    msrp: parseNumber(enr?.msrp ?? a.msrp),
    productFamily:
      a.grill_family ?? a.product_family ?? a.ProductFamily ?? enr?.grill_family ?? undefined,
    status: a.status ?? a.Status ?? 'Active',
    purchaseDate: a.install_date ?? a.InstallDate ?? a.purchasedate ?? undefined,
    registeredDate: a.created_date ?? a.CreatedDate ?? a.createddate,
    firmwareVersion: deviceBelongsToProfile
      ? (a.firmware_version ?? enr?.firmware_version)
      : undefined,
    configVersion: deviceBelongsToProfile ? (a.config_version ?? enr?.config_version) : undefined,
    deviceUuid:
      a.device_uuid ?? a.uuid ?? a.Uuid ?? a.uuid__c ?? enr?.device_uuid ?? undefined,
    deviceFriendlyName: deviceBelongsToProfile
      ? (a.device_friendly_name ?? enr?.device_friendly_name)
      : undefined,
    lastConnected: deviceBelongsToProfile
      ? (a.last_connected_date ?? a.last_connected ?? enr?.last_connected_date)
      : undefined,
    categoryLevels: levels.length ? levels : undefined,
    wifiName: deviceBelongsToProfile ? (a.wifi_name ?? enr?.wifi_name) : undefined,
    wifiSignalStrength: deviceBelongsToProfile
      ? (a.wifi_signal_strength ?? enr?.wifi_signal_strength)
      : undefined,
    // The /short formatter derives these from product_category_level_*; the
    // /full payload doesn't pre-derive them, so fall back to the underlying
    // category levels so the row renders for both shapes.
    controllerType:
      a.controller_type ??
      enr?.controller_type ??
      a.product_category_level_2 ??
      enr?.product_category_level_2 ??
      cat?.l2,
    controllerFamily: a.controller_family ?? enr?.controller_family,
    grillLine:
      a.grill_line ??
      enr?.grill_line ??
      a.product_category_level_3 ??
      enr?.product_category_level_3 ??
      cat?.series,
    modelFamily:
      a.model_family ??
      enr?.model_family ??
      a.product_category_level_4 ??
      enr?.product_category_level_4 ??
      cat?.model,
    isCourtesyItem: a.is_courtesy_item ?? enr?.is_courtesy_item,
    categoryL1: levels[0] ?? null,
    categoryL2: levels[1] ?? null,
    categorySeries: levels[2] ?? null,
    categoryModel: levels[3] ?? null,
    // Connected-device lookup by device_uuid. Same owner-mismatch guard as
    // the other device fields above — if the IoT account isn't this
    // profile's, the linked device data isn't theirs either.
    ...(deviceBelongsToProfile
      ? (() => {
          const uuid = a.device_uuid ?? a.uuid ?? a.Uuid ?? a.uuid__c ?? enr?.device_uuid ?? null;
          const dev = uuid ? deviceByUuid?.get(uuid) : undefined;
          if (!dev) return {};
          return {
            ownerName: dev.ownerName,
            ownerEmail: dev.ownerEmail,
            fleetEntryDate: dev.fleetEntryDate,
            manufacturedAt: dev.manufacturedAt,
            skillLevel: dev.skillLevel,
            timeZone: dev.timeZone,
            latitude: dev.latitude,
            longitude: dev.longitude,
            googleMapsLink: dev.googleMapsLink,
            totalCooks: dev.totalCooks,
          };
        })()
      : {}),
  };
}

function adaptCook(c: RawCookItem): CookViewItem {
  return {
    cookId: c.cook_id,
    startedAt: c.cook_started_datetime ?? c.started_at ?? c.cook_start_date ?? c.start_date ?? null,
    durationMinutes:
      (c.total_cook_duration_seconds ?? c.duration_seconds ?? null) != null
        ? Math.round((c.total_cook_duration_seconds ?? c.duration_seconds ?? 0) / 60)
        : null,
    ambientMin: c.min_ambient_temp ?? null,
    ambientMax: c.max_ambient_temp ?? null,
    probeMin: c.min_probe_temp ?? null,
    probeMax: c.max_probe_temp ?? null,
    goalTemp: c.avg_goal_temp ?? c.goal_temp ?? null,
    criticalAlerts: c.critical_alert_count ?? c.critical_alerts ?? 0,
    warningAlerts: c.warning_alert_count ?? c.warning_alerts ?? 0,
    lidOpenings: c.grill_lid_opening_count ?? c.lid_openings ?? 0,
    setpointChanges: c.number_of_setpoint_changes ?? c.setpoint_changes ?? 0,
    problemType: c.problem_cook_type ?? null,
    deviceFriendlyName: c.device_friendly_name ?? c.device_name ?? null,
    deviceTradeName: c.trade_name ?? null,
  };
}

function computeCookSummary(cooks: RawCookItem[]): CookSummary | undefined {
  if (!cooks.length) return undefined;
  let criticalAlerts = 0;
  let warningAlerts = 0;
  let problemCooks = 0;
  let totalDuration = 0;
  let durationCount = 0;
  let runningTotal: number | null = null;
  let lastCookDate: string | null = null;
  for (const c of cooks) {
    criticalAlerts += c.critical_alert_count ?? c.critical_alerts ?? 0;
    warningAlerts += c.warning_alert_count ?? c.warning_alerts ?? 0;
    if (c.problem_cook_type) problemCooks += 1;
    const dur = c.total_cook_duration_seconds ?? c.duration_seconds;
    if (typeof dur === 'number') {
      totalDuration += dur;
      durationCount += 1;
    }
    const started = c.cook_started_datetime ?? c.started_at;
    if (started && (!lastCookDate || started > lastCookDate)) lastCookDate = started;
    if (typeof c.device_running_total_cooks === 'number') {
      runningTotal = Math.max(runningTotal ?? 0, c.device_running_total_cooks);
    }
  }
  return {
    totalCooks: cooks.length,
    totalCriticalAlerts: criticalAlerts,
    totalWarningAlerts: warningAlerts,
    problemCooks,
    avgDurationMinutes: durationCount ? Math.round(totalDuration / durationCount / 60) : null,
    lastCookDate,
    runningTotalAllTime: runningTotal,
  };
}

export function toViewModel(p: CustomerProfile): MergedCustomer360 {
  const rawProfiles = p.raw_profiles ?? [];
  const attributes = p.attributes ?? {};
  const enrichments = p.asset_enrichments ?? [];
  const assetsSource = p.assets.length ? p.assets : enrichments;
  // When the backend ships raw Customer-Profiles assets (PascalCase) alongside
  // a parallel `asset_enrichments` array (Redshift-derived snake_case),
  // adaptAsset needs to look up enrichment by serial. Build the index once.
  // Server returns enrichments already collapsed to one-per-serial (row
  // selection + stale-controller swap happen in resolveEnrichments), so this
  // is a plain index.
  const enrichmentBySerial = new Map<string, CustomerProfile['assets'][number]>();
  for (const e of enrichments) {
    const serial = e.serial_number ?? e.SerialNumber ?? e.serialnumber;
    if (serial) enrichmentBySerial.set(serial, e);
  }

  const name =
    [p.first_name, p.last_name].filter(Boolean).join(' ').trim() ||
    attributes['Business Name'] ||
    rawProfiles.find((rp) => rp.BusinessName)?.BusinessName ||
    'Unknown customer';

  const mergedIds = p.profile_id
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const sfAttrs = rawProfiles.find((rp) => rp.Attributes?.sfdcAccountId)?.Attributes;
  const address = attributes['Mailing Address'] ?? pickAddress(rawProfiles);
  const accountType = attributes['Party Type'] ?? rawProfiles.find((rp) => rp.PartyType)?.PartyType;
  // Surface attributes that Search List shows but the agent-app sidebar
  // would otherwise drop (Business Name when distinct from the displayed
  // name, Personal Email when distinct from the primary email).
  const businessNameAttr =
    attributes['Business Name'] ?? rawProfiles.find((rp) => rp.BusinessName)?.BusinessName;
  const personalEmailAttr =
    attributes['Personal Email'] ??
    rawProfiles.find((rp) => rp.PersonalEmailAddress)?.PersonalEmailAddress;
  const distinctBusinessName =
    businessNameAttr && businessNameAttr !== name ? businessNameAttr : undefined;
  const distinctPersonalEmail =
    personalEmailAttr && !p.emails.includes(personalEmailAttr) ? personalEmailAttr : undefined;
  const accountNumber = p.account_number ?? undefined;

  return {
    identity: {
      name,
      emails: p.emails,
      phones: p.phones,
      address,
      accountType,
      accountNumber,
      businessName: distinctBusinessName,
      personalEmail: distinctPersonalEmail,
      sfAccountId: attributes.sfdcAccountId ?? sfAttrs?.sfdcAccountId,
      sfContactId: sfAttrs?.sfdcContactId,
      primaryProfileId: mergedIds[0] ?? '',
      mergedProfileIds: mergedIds,
    },
    stats: {
      totalCases: p.cases.length,
      openCases: p.cases.filter((c) => normalizeStatus(getCaseField(c, 'status')) === 'open')
        .length,
      registeredProducts: assetsSource.length,
      totalOrders: new Set(p.shipments.map((s) => s.order_number)).size,
    },
    cases: p.cases.map(adaptCase),
    orders: [],
    shipments: p.shipments.map(adaptShipment),
    // Adapt the connected devices first so we can index them by UUID and
    // merge their IoT-only fields into each asset (Search List uses one
    // unified Products view; the agent-app layouts now do the same).
    assets: (() => {
      const adaptedDevices = p.connected_devices.map(adaptDevice);
      const deviceByUuid = new Map<string, ConnectedDevice>();
      for (const d of adaptedDevices) {
        // controllerSerial holds the UUID when identifierKind === 'uuid';
        // also accept it as a serial→device fallback so explicit asset
        // serials match too.
        if (d.controllerSerial) deviceByUuid.set(d.controllerSerial, d);
      }
      const adapted = assetsSource.map((a) =>
        adaptAsset(a, enrichmentBySerial, p.emails?.[0], deviceByUuid)
      );
      // Synthesize entries for connected devices that don't show up as a
      // formal SF asset — otherwise the Devices-tab removal would lose
      // them entirely. These rows have the device fields populated but
      // little SF metadata (no purchase date, no warranty, etc.).
      const linkedUuids = new Set<string>();
      for (const x of adapted) {
        if (x.deviceUuid) linkedUuids.add(x.deviceUuid);
      }
      for (const d of adaptedDevices) {
        const uuid = d.controllerSerial;
        if (!uuid || linkedUuids.has(uuid)) continue;
        adapted.push({
          serialNumber: uuid,
          // Same convention as the SF-asset case: model in the header,
          // friendly name as the nickname (when distinct from the model).
          productName: d.tradeName ?? d.friendlyName ?? 'Connected device',
          nickname: d.friendlyName && d.friendlyName !== d.tradeName ? d.friendlyName : undefined,
          productFamily: d.tradeName,
          status: 'Active',
          deviceUuid: uuid,
          deviceFriendlyName: d.friendlyName,
          firmwareVersion: d.firmwareVersion ?? undefined,
          lastConnected: d.lastConnected ?? undefined,
          ownerName: d.ownerName,
          ownerEmail: d.ownerEmail,
          fleetEntryDate: d.fleetEntryDate,
          manufacturedAt: d.manufacturedAt,
          skillLevel: d.skillLevel,
          timeZone: d.timeZone,
          latitude: d.latitude,
          longitude: d.longitude,
          googleMapsLink: d.googleMapsLink,
          totalCooks: d.totalCooks,
        });
      }
      return adapted;
    })(),
    connectedDevices: p.connected_devices.map(adaptDevice),
    sourceProfiles: mergedIds.map((id) => {
      const rp = rawProfiles.find((r) => r.ProfileId === id);
      return {
        profileId: id,
        email: rp?.EmailAddress ?? rp?.PersonalEmailAddress,
        phone: rp?.PhoneNumber,
        hasName: Boolean(rp?.FirstName || rp?.LastName || p.first_name || p.last_name),
        caseCount: 0,
        assetCount: 0,
      };
    }),
    rawProfiles,
    cookSummary: computeCookSummary(p.cooks),
    cooks: p.cooks.map(adaptCook).sort((a, b) => {
      const at = a.startedAt ? new Date(a.startedAt).getTime() : 0;
      const bt = b.startedAt ? new Date(b.startedAt).getTime() : 0;
      return bt - at;
    }),
  };
}
